import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogMessageComponent } from './DialogDataExampleDialog';
import { MatDialog } from '@angular/material/dialog';
import { Capacitor } from '@capacitor/core';

const MOBILE_MAX_WIDTH = 820;
const TABLET_MAX_WIDTH = 1366;
const COMPACT_MAX_WIDTH = 1100;

@Injectable({
  providedIn: 'root',
})
export class ToolsService {
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  private isNativeMobilePlatform() {
    const platform = Capacitor.getPlatform(); // 'web', 'ios', 'android'
    return (
      Capacitor.isNativePlatform() &&
      (platform === 'ios' || platform === 'android')
    );
  }

  private hasCoarsePointer() {
    return (
      window.matchMedia('(any-pointer: coarse)').matches ||
      window.matchMedia('(pointer: coarse)').matches ||
      navigator.maxTouchPoints > 0
    );
  }

  isMobile() {
    if (this.isNativeMobilePlatform()) {
      return true;
    }

    return window.innerWidth <= MOBILE_MAX_WIDTH && this.hasCoarsePointer();
  }

  isTablet() {
    if (this.isNativeMobilePlatform()) {
      return false;
    }

    const width = window.innerWidth;
    return (
      width > MOBILE_MAX_WIDTH &&
      width <= TABLET_MAX_WIDTH &&
      this.hasCoarsePointer()
    );
  }

  isCompact() {
    return (
      this.isNativeMobilePlatform() ||
      window.innerWidth <= COMPACT_MAX_WIDTH ||
      this.isTablet()
    );
  }

  toastError(
    content: string,
    duration: number = 2000,
    className: string = 'app-toast'
  ) {
    this.snackBar.open(content, 'Ok', {
      duration: duration,
      panelClass: [className, 'app-toast-center'],
      horizontalPosition: 'center',
      verticalPosition: this.isMobile() ? 'top' : 'bottom',
    });
  }

  launchErrorAlert(
    content: string,
    title = 'Erreur !',
    useHtmlContent = false
  ) {
    this.dialog.open(DialogMessageComponent, {
      data: { title, message: content, buttons: 'Fermer', useHtmlContent },
    });
  }

  launchAlert(
    header: string,
    message: string,
    buttons: any,
    columnForButton: boolean = false,
    useHtmlContent = false
  ) {
    this.dialog.open(DialogMessageComponent, {
      data: {
        title: header,
        message: message,
        buttons: buttons,
        columnForButton: columnForButton,
        useHtmlContent,
      },
    });
  }

  launchInfoAlert(
    content: string,
    title = 'Informations',
    useHtmlContent = false
  ) {
    this.dialog.open(DialogMessageComponent, {
      data: { title, message: content, buttons: 'Ok', useHtmlContent },
    });
  }
}

export const matchMedia = (win: Window, query: string): boolean =>
  win.matchMedia?.(query).matches;
